/*
 * CERTIFICATE ENTITY FIELD MAPPING (`cr4cc_certificatelists`) - TO BE CREATED:
 * LEFT: cr4cc_title (Certificate #) | MIDDLE: cr4cc_field_1 (Project Location) | RIGHT: cr4cc_field_5 (Issue Date)
 * STATUS: cr4cc_field_6 ("1"=Draft, "2"=Pending, "3"=Issued, "4"=Expired)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Pencil } from 'lucide-react';
import { useNotificationSound } from '../../hooks/useNotificationSound.js';
import { useAuth } from '@/components/Auth/AuthContext';
import PageHeader from '@/components/Layout/PageHeader';
import { useIsNarrowScreen } from '@/hooks/useMediaQuery';
import { getPrimaryRoleLabel } from '@/utils/swaRole';
import { replaceInternalOwnerEmails } from '@/utils/internalOwnerLabel';
import { DEFAULT_LIST_PREVIEW_LIMIT, getVisibleListRows } from '@/constants/listDisplay';
import { LIST_SEARCH_DEBOUNCE_MS } from '@/constants/search';
import { Button, Card, DataRow, EmptyState, PageSection, Select, StatusBadge } from '@/ui/components';
import '@/pages/dataGridFocus.css';

const WILSON_EMAIL = 'wd@linfinityeng.com';
type CertificateSortBy = 'number' | 'issueDate' | 'status';
type SortDirection = 'asc' | 'desc';
const LIST_PAGE_SIZE = 20;

function getDefaultSortDirection(sortBy: CertificateSortBy): SortDirection {
  return 'desc';
}

function isOpenableFileLink(value?: string | null) {
  const normalized = String(value || '').trim();
  return /^(https?:\/\/|\/|blob:)/i.test(normalized);
}

const CertificateList: React.FC = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'pending' | 'issued' | 'expired'>('all');
  const [sortBy, setSortBy] = useState<CertificateSortBy>('issueDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>(getDefaultSortDirection('issueDate'));
  const { playSuccess, playError } = useNotificationSound();
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const isNarrowScreen = useIsNarrowScreen(640);
  const currentUserEmail = (user?.userDetails || '').trim().toLowerCase();
  const isCivilCertificateUser = currentUserEmail === WILSON_EMAIL;
  const pageTitle = isCivilCertificateUser ? 'Civil Certificates' : 'Certificates';
  const pageSubtitle = isCivilCertificateUser
    ? 'Review civil certificates and track their issue status.'
    : 'Review certificates and track their issue status.';

  const roleLabel = getPrimaryRoleLabel(user?.userRoles, user?.userDetails);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadCertificates(searchQuery.trim());
    }, searchQuery.trim() ? LIST_SEARCH_DEBOUNCE_MS : 0);

    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const loadCertificates = async (query: string = searchQuery.trim()) => {
    setLoading(true);
    setError(null);

    try {
      console.log('=== STARTING CERTIFICATE LOAD ===');
      console.log('User authenticated:', !!user);

      // Dynamic import to ensure AuthContext is initialized first
      const { azureSqlService } = await import('@/services/azureSqlService.js');

      // Get certificates from Azure SQL Database
      const certificateData = await azureSqlService.getCertificates({
        page: 1,
        pageSize: LIST_PAGE_SIZE,
        query: query ? { q: query } : null,
        useCache: !query,
      });

      console.log('=== API RESPONSE ===');
      console.log('Certificates data:', certificateData);
      console.log('Certificates array length:', Array.isArray(certificateData) ? certificateData.length : 'NOT_ARRAY');

      // Map API response to component format
      const mappedCertificates = certificateData.map((cert: any) => ({
        id: (cert.CertificateId ?? cert.CertificateID ?? cert.certificateId ?? cert.id ?? cert.CertificateNumber ?? cert.certificateNumber)?.toString(),
        certificateNumber: cert.CertificateNumber ?? cert.certificateNumber,
        projectNumber: cert.ProjectNumber ?? cert.ProjectCode ?? cert.projectNumber ?? cert.projectCode ?? '',
        projectLocation: replaceInternalOwnerEmails(
          cert.ProjectLocation
          || cert.ProjectAddress
          || cert.Address
          || cert.SiteAddress
          || cert.projectLocation
          || cert.projectAddress
          || cert.address
          || 'N/A'
        ),
        certificateType: replaceInternalOwnerEmails(cert.CertificateType || 'Engineering Certificate'),
        issueDate: cert.IssueDate,
        status: cert.Status || 'Draft',
        statusCode: cert.StatusCode || '1',
        clientName: replaceInternalOwnerEmails(cert.ClientName || 'N/A'),
        companyName: replaceInternalOwnerEmails(cert.CompanyName || 'N/A'),
        fee: cert.Fee || '$0.00',
        issuedFileUrl: cert.IssuedFileUrl || cert.OutputPath || null,
      }));

      setCertificates(mappedCertificates);
      playSuccess();
      console.log('✅ Certificates loaded successfully from Azure SQL:', mappedCertificates.length);
    } catch (err) {
      console.error('❌ Error loading certificates:', err);
      console.error('Error type:', typeof err);
      console.error('Error stack:', err?.stack);

      // Extract detailed error information
      let errorMessage = 'Unknown error';
      let errorDetails = null;

      if (err instanceof Error) {
        errorMessage = err.message;
        // Check if error has additional details from API response
        if ((err as any).details) {
          errorDetails = (err as any).details;
          console.error('Error details from API:', errorDetails);

          // Use server message if available
          if (errorDetails.message) {
            errorMessage = errorDetails.message;
          }
        }
      } else {
        errorMessage = String(err);
      }

      setError(errorMessage);
      playError();
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const isDraftCertificate = (certificate: any) => String(certificate.statusCode || '').trim().toLowerCase() === '1';

  const openCertificateEditor = (certificate: any) => {
    navigate('/certificates/workflow', {
      state: {
        mode: 'amend',
        certificateId: certificate.id || undefined,
        certificateNumber: certificate.certificateNumber || undefined,
      },
    });
  };

  const getIssuedFileUrl = (certificate: any) => String(certificate.issuedFileUrl || '').trim();

  const canOpenIssuedFile = (certificate: any) => isOpenableFileLink(getIssuedFileUrl(certificate));

  const openIssuedCertificateFile = (certificate: any) => {
    const fileUrl = getIssuedFileUrl(certificate);
    if (!isOpenableFileLink(fileUrl)) {
      return;
    }

    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  };

  // Filter certificates based on search query
  const filteredCertificates = certificates
    .filter((certificate: any) => {
      if (statusFilter === 'all') return true;
      const code = String(certificate.statusCode || '').toLowerCase();
      if (statusFilter === 'issued') return code === '3';
      if (statusFilter === 'pending') return code === '2';
      if (statusFilter === 'expired') return code === '4';
      return code === '1';
    })
    .filter((certificate: any) => {
      if (searchQuery.trim() === '') return true;
      const q = searchQuery.toLowerCase();
      return (
        certificate.certificateNumber?.toLowerCase().includes(q) ||
        certificate.projectNumber?.toLowerCase().includes(q) ||
        certificate.projectLocation?.toLowerCase().includes(q) ||
        certificate.clientName?.toLowerCase().includes(q) ||
        certificate.companyName?.toLowerCase().includes(q) ||
        certificate.certificateType?.toLowerCase().includes(q)
      );
    });

  const sortedCertificates = [...filteredCertificates].sort((a: any, b: any) => {
    let comparison = 0;
    switch (sortBy) {
      case 'number':
        comparison = String(a.certificateNumber || '').localeCompare(String(b.certificateNumber || ''));
        break;
      case 'status':
        comparison = String(a.statusCode || '').localeCompare(String(b.statusCode || ''));
        break;
      default: {
        const da = new Date(a.issueDate || 0).getTime();
        const db = new Date(b.issueDate || 0).getTime();
        comparison = da - db;
        break;
      }
    }

    return sortDirection === 'desc' ? comparison * -1 : comparison;
  });
  const visibleCertificates = getVisibleListRows(sortedCertificates, searchQuery);
  const isPreviewingCertificates = searchQuery.trim() === '' && sortedCertificates.length > visibleCertificates.length;

  const handleSortChange = (value: string) => {
    const nextSortBy = value as CertificateSortBy;
    setSortBy(nextSortBy);
    setSortDirection(getDefaultSortDirection(nextSortBy));
  };

  const toggleSortDirection = () => {
    setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
  };

  if (loading) {
    return (
      <div className="dgf-page">
        <PageHeader
          title={pageTitle}
          subtitle={pageSubtitle}
          itemCount={0}
          gradientColor="linear-gradient(135deg, #173b84 0%, #2251b4 54%, #4b6fbc 100%)"
          icon="🏆"
          userLabel={user?.userDetails}
          roleLabel={roleLabel}
          searchEnabled={false}
          sortEnabled={false}
        />
        <PageSection width="wide" padding="default">
          <Card padding="lg">
            <div className="dgf-state-card">
              <div className="dgf-state-card__icon">⏳</div>
              <div>Loading certificates...</div>
            </div>
          </Card>
        </PageSection>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dgf-page">
        <PageHeader
          title={pageTitle}
          subtitle={pageSubtitle}
          itemCount={0}
          gradientColor="linear-gradient(135deg, #173b84 0%, #2251b4 54%, #4b6fbc 100%)"
          icon="🏆"
          userLabel={user?.userDetails}
          roleLabel={roleLabel}
          searchEnabled={false}
          sortEnabled={false}
        />
        <PageSection width="wide" padding="default">
          <Card padding="lg">
            <div className="dgf-state-card">
              <div className="dgf-state-card__icon">⚠️</div>
              <div className="dgf-state-card__title">Error Loading Certificates</div>
              <div className="dgf-notice dgf-notice--danger" style={{ marginTop: 16 }}>{error}</div>
              <div className="dgf-state-card__actions">
                <Button onClick={() => loadCertificates()} variant="secondary">
                  🔄 Retry
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      await login();
                      setTimeout(() => loadCertificates(), 1000);
                    } catch (err) {
                      console.error('Re-authentication failed:', err);
                    }
                  }}
                  variant="primary"
                >
                  🔑 Re-authenticate
                </Button>
              </div>
            </div>
          </Card>
        </PageSection>
      </div>
    );
  }

  return (
    <div className="dgf-page">
      <PageHeader
        title={pageTitle}
        subtitle={pageSubtitle}
        itemCount={visibleCertificates.length}
        gradientColor="linear-gradient(135deg, #173b84 0%, #2251b4 54%, #4b6fbc 100%)"
        icon="🏆"
        userLabel={user?.userDetails}
        roleLabel={roleLabel}
        searchEnabled={true}
        searchQuery={searchQuery}
        searchPlaceholder="Search certificates"
        onSearchChange={setSearchQuery}
        sortEnabled={true}
        sortBy={sortBy}
        sortDirection={sortDirection}
        sortOptions={[
          { value: 'issueDate', label: 'Issue date' },
          { value: 'number', label: 'Number' },
          { value: 'status', label: 'Status' },
        ]}
        onSortChange={handleSortChange}
        onSortDirectionToggle={toggleSortDirection}
        searchActions={(
          <Button
            onClick={() => navigate('/certificates/workflow')}
            variant="secondary"
            fullWidth={isNarrowScreen}
          >
            Workflow
          </Button>
        )}
        filters={(
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            fullWidth
            options={[
              { value: 'all', label: 'All statuses' },
              { value: 'draft', label: 'Draft' },
              { value: 'pending', label: 'Pending' },
              { value: 'issued', label: 'Issued' },
              { value: 'expired', label: 'Expired' },
            ]}
          />
        )}
      />

      {/* Certificate List */}
      {!loading && visibleCertificates.length > 0 && (
        <PageSection width="wide" padding="default">
          {isPreviewingCertificates ? (
            <div className="dgf-list-summary">
              Showing the latest {DEFAULT_LIST_PREVIEW_LIMIT} of {sortedCertificates.length} certificates. Use search to find older records.
            </div>
          ) : null}
          <div className="certificate-mobile-list">
            {visibleCertificates.map((certificate: any) => (
              <DataRow
                key={certificate.id}
                className="certificate-data-row"
                density="compact"
                onClick={() => openCertificateEditor(certificate)}
                title={(
                  <div className="certificate-data-row__title-line">
                    <span className="certificate-data-row__number">{certificate.certificateNumber}</span>
                    {certificate.projectNumber ? (
                      <span className="certificate-data-row__project-number">{certificate.projectNumber}</span>
                    ) : null}
                    <span className="certificate-data-row__location">{certificate.projectLocation}</span>
                  </div>
                )}
                subtitle={certificate.certificateType}
                description={(
                  <span className="certificate-data-row__compact-meta">
                    {[
                      `Client: ${certificate.clientName}`,
                      `Company: ${certificate.companyName}`,
                      `Fee: ${certificate.fee}`,
                      `Issue: ${formatDate(certificate.issueDate)}`,
                    ].join('   ')}
                  </span>
                )}
                amount={canOpenIssuedFile(certificate) ? (
                  <div className="certificate-data-row__amount-line">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      shape="pill"
                      className="certificate-data-row__action"
                      iconBefore={<ExternalLink size={14} aria-hidden="true" />}
                      aria-label={`View issued certificate ${certificate.certificateNumber || ''}`.trim()}
                      title="View issued certificate"
                      onClick={(event) => {
                        event.stopPropagation();
                        openIssuedCertificateFile(certificate);
                      }}
                    >
                      <span className="certificate-data-row__action-label">View issued</span>
                    </Button>
                  </div>
                ) : null}
                status={(
                  <div className="certificate-data-row__status-line">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      shape="pill"
                      className="certificate-data-row__action"
                      iconBefore={<Pencil size={14} aria-hidden="true" />}
                      aria-label={`${isDraftCertificate(certificate) ? 'Edit' : 'Amend'} certificate ${certificate.certificateNumber || ''}`.trim()}
                      title={isDraftCertificate(certificate) ? 'Edit certificate' : 'Amend certificate'}
                      onClick={(event) => {
                        event.stopPropagation();
                        openCertificateEditor(certificate);
                      }}
                    >
                      <span className="certificate-data-row__action-label">
                        {isDraftCertificate(certificate) ? 'Edit' : 'Amend'}
                      </span>
                    </Button>
                    <StatusBadge status={certificate.status} size="sm">{certificate.status}</StatusBadge>
                  </div>
                )}
              />
            ))}
          </div>
        </PageSection>
      )}

      {/* Empty State */}
      {!loading && visibleCertificates.length === 0 && !error && (
        <PageSection width="wide" padding="default">
          <Card padding="none">
            <EmptyState
              icon={<span style={{ fontSize: '64px' }}>🏆</span>}
              title="No certificates found"
              description="No certificate records matched the current source and filters. Check the active SQL source and browser console logs if this looks unexpected."
              action={(
                <Button type="button" variant="secondary" onClick={() => navigate('/certificates/workflow')}>
                  Open workflow
                </Button>
              )}
            />
          </Card>
        </PageSection>
      )}

      {/* Debug Info - Simple */}
      {!loading && filteredCertificates.length > 0 && (
        <PageSection width="wide" padding="default">
          <Card padding="sm">
            <div className="dgf-notice dgf-notice--info">
              🏆 {filteredCertificates.length} certificates loaded
            </div>
          </Card>
        </PageSection>
      )}
    </div>
  );
};

export default CertificateList;
